import {Injectable} from '@angular/core'
import {Router} from '@angular/router'
import {AngularFireDatabase} from 'angularfire2/database'
import {Subject, BehaviorSubject} from 'rxjs'
import 'rxjs/add/operator/first'
import {UserService} from '../user'
import * as firebase from 'firebase'
import {Folder} from './folder'
import {LoaderService} from '../ui-shared/loader/loader.service'

const FOLDERCOLLECTION: string = 'folders'
const PENDINGCOLLECTION: string = 'pending'
const VERSIONCOLLECTION: string = 'versions'
const MESSAGECOLLECTION: string = 'messages'
const CUSTOMURLCOLLECTION: string = 'customURLs'

@Injectable()
export class FolderService {
  public user: any
  public current: any
  private accessibleFolders = new Subject()
  private accessibleParents = new Subject()
  private ownerLevel = new Subject()
  private viewerLevel = new Subject()

  constructor(
    private db: AngularFireDatabase,
    private router: Router,
    private userService: UserService,
    private loader: LoaderService
  ) {
    this.init()
    this.userService.onSignOut.subscribe(() => this.init())
  }

  init() {
    if (this.userService.current && this.userService.current.username) {
      this.user = this.userService.current
      this.user.folders = Object.values(this.user.folders || {})
    } else {
      this.userService.get.subscribe(user => {
        this.user = user
        this.user.folders = Object.values(this.user.folders || {})
      })
    }
  }

  createFolder(parentId: string, name: string, autoopen: boolean = true) {
    if (!this.user) return

    this.loader.start('Creating new folder...')

    let newfolder = new Folder()
    let sub = this.getFolderFromId(parentId).subscribe(folder => {
      sub.unsubscribe()
      newfolder.created_by = {
        id: this.user.uid,
        name: this.user.username,
      }
      newfolder.date = firebase.database.ServerValue.TIMESTAMP
      newfolder.name = name
      newfolder.parents = folder['parents']
      newfolder.access = folder['access']
      newfolder.branding = folder['branding'] || {}
      newfolder.access.public = true
      if (folder['settings']) newfolder.settings = folder['settings']

      newfolder.parents.push({
        id: folder['id'],
        name: folder['name'],
      })

      for (let i in newfolder.access['read']) {
        newfolder.access['read'][i]['level'] += 1
      }

      for (let i in newfolder.access['write']) {
        newfolder.access['write'][i]['level'] += 1
      }

      let ref = this.db
        .list(FOLDERCOLLECTION)
        .push(newfolder)
        .then(
          ref => {
            newfolder.id = ref.key
            ref.set(newfolder)
            let update_parent = {}
            update_parent[newfolder.id] = newfolder.name

            ref.update({id: ref.key})
            this.db
              .object([FOLDERCOLLECTION, folder['id'], 'folders'].join('/'))
              .update(update_parent)
              .then(() => {
                this.loader.stop()
                this.router.navigate(['/folder/', ref.key])
              })
          },
          error => {
            this.loader.stop()
            console.error(error)
          }
        )
    })
  }

  // orderBy(criteria: string, reverse: boolean = false) {
  // 	this.current._files_array = this.current.filtered_files_array.sort((a, b) => {
  // 		if (a[criteria] < b[criteria]) return reverse ? -1 : 1;
  // 		else if (a[criteria] > b[criteria]) return reverse ? 1 : -1;
  // 		else return 0;
  // 	})
  // }

  filter(needle: string) {
    this.current.filtered_files_array = this.current.files_array.filter(a =>
      needle
        .split(' & ')
        .every(_needle =>
          a.name.match(new RegExp(_needle.length > 1 ? _needle : '', 'i'))
        )
    )

    this.current.filtered_subfolders = []
    for (let id in this.current.folders) {
      if (
        this.current.folders[id].match(
          new RegExp(needle.length > 1 ? needle : '', 'i')
        )
      )
        this.current.filtered_subfolders.push(id)
    }
    this.current.filtered_subfolders.sort((a, b) => {
      return this.current.folders[a] > this.current.folders[b]
    })
    this.current.subject.next()
  }

  getAccessibleFolders(): Subject<{}> {
    if (!this.user) {
      this.waitForUserInfos(() => {
        this.accessibleFolders.next(this.user.folders)
      })
      return this.accessibleFolders
    }

    return new BehaviorSubject(this.user.folders)
  }

  getAccessibleParents(folder: any): Subject<{}> {
    if (!this.user) {
      this.waitForUserInfos(() => {
        this.getAccessibleParents(folder)
      })
      return this.accessibleParents
    }
    let read =
      folder.access &&
      folder.access.read &&
      folder.access.read[this.user.uid] &&
      folder.access.read[this.user.uid].level
        ? folder.access.read[this.user.uid].level
        : 1
    let write =
      folder.access &&
      folder.access.write &&
      folder.access.write[this.user.uid] &&
      folder.access.write[this.user.uid].level
        ? folder.access.write[this.user.uid].level
        : 1
    let level = Math.max(read, write) - 1

    let folders = level > 0 ? folder.parents.slice(-level) : []

    this.accessibleParents.next(folders)
    return new BehaviorSubject(folders)
  }

  getFolderFromId(folderId: string) {
    return this.db.object([FOLDERCOLLECTION, folderId].join('/')).valueChanges()
  }

  getOwnerLevel(folder: any): Subject<{}> {
    if (!this.user) {
      this.waitForUserInfos(() => {
        this.getOwnerLevel(folder)
      })
      return this.ownerLevel
    }

    let level =
      folder.access &&
      folder.access.write &&
      folder.access.write[this.user.uid] &&
      folder.access.write[this.user.uid].level
        ? folder.access.write[this.user.uid].level
        : 0

    this.ownerLevel.next(level)

    return new BehaviorSubject(level)
  }

  getMessages(folderId: string, fileId: string) {
    folderId = folderId || this.current.id
    let path = [MESSAGECOLLECTION, folderId]
    if (fileId) path.push(fileId)
    return this.db.list(path.join('/')).valueChanges()
  }

  getViewerLevel(folder: any): Subject<{}> {
    if (!this.user) {
      this.waitForUserInfos(() => {
        this.getViewerLevel(folder)
      })
      return this.viewerLevel
    }

    let read_level =
      folder.access &&
      folder.access.read &&
      folder.access.read[this.user.uid] &&
      folder.access.read[this.user.uid].level
        ? folder.access.read[this.user.uid].level
        : 0

    let write_level =
      folder.access &&
      folder.access.write &&
      folder.access.write[this.user.uid] &&
      folder.access.write[this.user.uid].level
        ? folder.access.write[this.user.uid].level
        : 0

    let level = read_level > write_level ? read_level : write_level
    this.viewerLevel.next(level)

    return new BehaviorSubject(level)
  }

  update(folderId: string, infos: any): Promise<void> {
    return this.db.object([FOLDERCOLLECTION, folderId].join('/')).update(infos)
  }

  createPushId(): string {
    return this.db.createPushId()
  }

  updateAccess() {
    if (!this.current) return
    return this.db
      .object([FOLDERCOLLECTION, this.current.id].join('/'))
      .update({
        access: this.current.access,
      })
  }

  // updateVersion() {
  // 	if (!this.current) return
  // 	return this.db.object([VERSIONCOLLECTION, this.current.id].join('/')).update({
  // 		access: this.current.access,
  // 	})
  // }

  updateSettings() {
    if (!this.current) return
    return this.db
      .object([FOLDERCOLLECTION, this.current.id].join('/'))
      .update({
        settings: this.current.settings,
      })
  }

  updateName() {
    if (!this.current) return
    return this.db
      .object([FOLDERCOLLECTION, this.current.id].join('/'))
      .update({
        name: this.current.name,
      })
  }

  updateBranding(folder_id, new_branding, original_branding) {
    let foldersub = this.getFolderFromId(folder_id).subscribe(folder => {
      foldersub.unsubscribe()

      if (!original_branding) original_branding = folder['branding']

      if (
        !folder['branding'] ||
        JSON.stringify(original_branding) == JSON.stringify(folder['branding'])
      ) {
        // don't change branding if it was unique
        this.db.object([FOLDERCOLLECTION, folder_id].join('/')).update({
          branding: new_branding,
        })
      } else {
        console.log('ignore branding')
      }

      for (let subfolderid in folder['folders']) {
        this.updateBranding(subfolderid, new_branding, original_branding)
      }
    })
  }

  addPending(email, access) {
    if (!this.current) return
    let key = btoa(email)
    console.log('invite', email, access)
    this.db
      .object(
        [PENDINGCOLLECTION, btoa(email), this.current.id, access].join('/')
      )
      .set(true)
    if (!this.current.access) this.current.access = {}
    if (!this.current.access[access]) this.current.access[access] = {}
    this.current.access[access][key] = {
      name: email,
      level: 1,
    }
    return this.updateAccess()
  }

  approveFile(index, value) {
    this.db
      .object(
        [FOLDERCOLLECTION, this.current.id, 'files', index, 'approved'].join(
          '/'
        )
      )
      .set(value)
  }

  deleteFiles(files: Array<any>): Promise<boolean> {
    var c = confirm('Do you want to archive these files?')
    if (!c) return
    this.loader.start('Preparing to delete...')
    return new Promise((resolve, reject) => {
      let _ = this
      let database_files_ref = this.current.files
      let files_to_delete = []

      for (let i = 0; i < files.length; i++) {
        let file = files[i]
        delete database_files_ref[file.dbkey]
        // database_files_ref = database_files_ref.filter(e => e.ref !== file.ref)
        files_to_delete = files_to_delete.concat(
          file.files ? file.files : file.ref
        )
      }

      let remaining_files = files_to_delete.length

      _.loader.update(`Deleting ${remaining_files} files...`, 0)

      for (let i in files_to_delete) {
        let file = files_to_delete[i]
        firebase
          .storage()
          .ref()
          .child(file)
          .delete()
          .then(function () {
            _.loader.update(
              `Deleting ${remaining_files} files...`,
              (files_to_delete.length - remaining_files) /
                files_to_delete.length
            )
            if (--remaining_files == 0) {
              console.log('all files deleted')
              updateRef()
            }
          })
          .catch(function (error) {
            console.error(error)
            _.loader.update(
              `Deleting ${remaining_files} files...`,
              (files_to_delete.length - remaining_files) /
                files_to_delete.length
            )
            console.error(error)
            if (--remaining_files == 0) {
              console.log('all files deleted')
              updateRef()
            }
          })
      }

      function updateRef() {
        _.loader.update(`Updating database after deletion...`, 1)
        _.db
          .object([FOLDERCOLLECTION, _.current.id, 'files'].join('/'))
          .set(database_files_ref)
          .then(() => {
            _.loader.stop()
            resolve()
          })
      }
    })
  }

  // Recursively delete a folder
  delete(folderId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // Get folder informations once
      let sub = this.getFolderFromId(folderId).subscribe(folder => {
        sub.unsubscribe()

        this.loader.update(`Deleting folder "${folder['name']}"`, 0)

        // Create scope for `this`
        let _ = this
        // Countdown for remaining items to delete
        let remaining_files = 0
        let remaining_folders = folder['folders']
          ? Object.keys(folder['folders']).length
          : 0

        // Delete files
        let files = []

        // list of ALL the files to delete within that folder. Flatten if it's a banner
        for (let i in folder['files']) {
          if (folder['files'][i].files) {
            remaining_files += folder['files'][i].files.length
            files = files.concat(folder['files'][i].files)
          } else {
            remaining_files++
            files.push(folder['files'][i].ref || folder['files'][i])
          }
        }

        for (let i in files) {
          let file = files[i]
          firebase
            .storage()
            .ref()
            .child(file)
            .delete()
            .then(function () {
              if (--remaining_files == 0)
                console.log('all files deleted in this folder')
            })
            .catch(function (error) {
              console.error(error)
              if (--remaining_files == 0)
                console.log('all files deleted in this folder')
            })
        }

        // Delete subfolders
        if (remaining_folders == 0) {
          deleteRef()
        } else {
          for (let key in folder['folders']) {
            this.delete(key).then(() => {
              if (--remaining_folders <= 0) deleteRef()
            })
          }
        }

        // Delete reference in parent's subfolder, and self reference
        function deleteRef() {
          let parents = folder['parents']
          if (parents) {
            let parent = parents.slice(-1)[0]
            _.db
              .object(
                [FOLDERCOLLECTION, parent.id, 'folders', folderId].join('/')
              )
              .remove()
              .then(() => {
                _.db
                  .object([FOLDERCOLLECTION, folderId].join('/'))
                  .remove()
                  .then(() => {
                    resolve()
                  })
              })
          }
        }
      })
    })
  }

  checkURL(urlstring: string) {
    let url = new Subject()
    urlstring = urlstring.toLowerCase()
    let url_sub = this.db
      .object([CUSTOMURLCOLLECTION, urlstring].join('/'))
      .valueChanges()
      .subscribe(id => {
        url_sub.unsubscribe()
        // console.log(id)
        url.next(id)
      })
    return url
  }

  getIdFromUrl(url: string) {
    return this.db.object([CUSTOMURLCOLLECTION, url].join('/')).valueChanges()
  }

  updateURL(folder: Folder, newURL: string) {
    if (folder.customURL) {
      this.db.object([CUSTOMURLCOLLECTION, folder.customURL].join('/')).remove()
    }
    if (newURL && newURL != '')
      this.db.object([CUSTOMURLCOLLECTION, newURL].join('/')).set(folder.id)
    this.db
      .object([FOLDERCOLLECTION, folder.id, 'customURL'].join('/'))
      .set(newURL)
  }

  waitForUserInfos(callback) {
    this.userService.get.subscribe(u => callback())
  }
}



// WEBPACK FOOTER //
// /Users/axel/Sites/Bannerboy/Internal/attaboy-angular/src/app/folder/folder.service.ts